import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RotateCcw, Send, Sparkles } from "lucide-react";
import { api, streamInterview } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function Interview() {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [building, setBuilding] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const started = useRef(false);
  const endRef = useRef(null);
  const navigate = useNavigate();

  const scroll = () => endRef.current?.scrollIntoView({ behavior: "smooth" });

  const run = async (text) => {
    setStreaming(true);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      await streamInterview(text, (d) =>
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + d };
          return copy;
        })
      );
    } catch (e) {
      toast.error(e.message || "Loop could not respond");
      setMessages((m) => m.filter((x, i) => !(i === m.length - 1 && !x.content)));
    } finally {
      setStreaming(false);
      setTimeout(scroll, 60);
    }
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      const { messages: existing } = await api.get("/interview/messages");
      setMessages(existing.map(({ role, content }) => ({ role, content })));
      setLoaded(true);
      if (!existing.length) run("");
    })();
  }, []);

  useEffect(scroll, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || streaming) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    track("interview_answer_sent", { length: text.length });
    await run(text);
  };

  const buildProfile = async () => {
    setBuilding(true);
    try {
      await api.post("/profile/synthesize");
      toast.success("Profile built — review what Loop learned");
      navigate("/profile");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBuilding(false);
    }
  };

  const reset = async () => {
    await api.post("/interview/reset");
    setMessages([]);
    run("");
  };

  const answers = messages.filter((m) => m.role === "user").length;

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="interview-page">
      <div className="lg:col-span-8 panel flex flex-col h-[calc(100vh-9rem)]">
        <div className="px-8 py-5 border-b border-white/[0.07] flex items-center gap-4">
          <span className="h-2 w-2 rounded-full bg-primary shadow-[0_0_12px_hsl(187_72%_58%)]" />
          <div>
            <p className="font-display text-base">Loop is interviewing you</p>
            <p className="text-xs text-muted-foreground mt-0.5">One question at a time. Skip anything.</p>
          </div>
          <button
            onClick={reset}
            data-testid="reset-interview-button"
            className="ml-auto flex items-center gap-2 text-xs text-muted-foreground hover:text-white transition-colors duration-200"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Start over
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-6" data-testid="interview-messages">
          {!loaded && <p className="overline">Loading conversation</p>}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
              <div
                className={
                  m.role === "user"
                    ? "max-w-[80%] px-5 py-3.5 bg-white/[0.06] border border-white/10 rounded-sm text-sm leading-relaxed"
                    : "max-w-[85%]"
                }
                data-testid={`msg-${m.role}-${i}`}
              >
                {m.role === "assistant" && <p className="overline mb-2">Loop</p>}
                <p className={m.role === "assistant" ? "font-display text-lg leading-relaxed" : ""}>
                  {m.content || (streaming ? "…" : "")}
                </p>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <div className="px-8 py-5 border-t border-white/[0.07] flex items-end gap-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder="Answer honestly — vague answers get vague options."
            data-testid="interview-input"
            className="flex-1 resize-none bg-transparent border border-white/10 rounded-sm px-4 py-3 text-sm outline-none focus:border-primary/60 transition-colors duration-200 placeholder:text-muted-foreground/70"
          />
          <button
            onClick={send}
            disabled={streaming || !draft.trim()}
            data-testid="send-message-button"
            className="p-3.5 rounded-sm bg-primary text-primary-foreground disabled:opacity-30 hover:bg-primary/90 transition-colors duration-200"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>

      <aside className="lg:col-span-4 space-y-4">
        <div className="panel p-8">
          <span className="overline">Progress</span>
          <p className="mt-5 font-display text-4xl tracking-tight" data-testid="answer-count">
            {answers}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">answers given</p>
          <div className="mt-6 h-1 bg-white/[0.07]">
            <div
              className="h-full bg-primary transition-[width] duration-500"
              style={{ width: `${Math.min(100, (answers / 8) * 100)}%` }}
            />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {answers < 5 ? `${5 - answers} more before a profile gets useful.` : "Enough to build a real profile."}
          </p>
        </div>

        <button
          onClick={buildProfile}
          disabled={building || answers === 0}
          data-testid="build-profile-button"
          className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-primary text-primary-foreground font-medium disabled:opacity-30 hover:bg-primary/90 hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
        >
          <Sparkles className="h-4 w-4" />
          {building ? "Reading you back…" : "Finish & build my profile"}
        </button>

        <div className="panel p-8">
          <span className="overline">You are in control</span>
          <ul className="mt-5 space-y-3 text-sm text-muted-foreground leading-relaxed">
            <li>Stop the interview whenever you like.</li>
            <li>Loop never recommends anything until you approve your profile.</li>
            <li>You can edit every single thing it learned.</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}
